import React from 'react';
import { Link } from 'react-router-dom';
import { BoxesIcon, PuzzleIcon, BookOpenIcon, BotIcon, MessageSquareIcon, CpuIcon, AlertTriangleIcon, PlusIcon, ArrowRightIcon } from 'lucide-react';
import { useScreenInit } from '../useScreenInit';
import { useModelStore } from '../stores/modelStore';
import { useSkillStore } from '../stores/skillStore';
import { useKnowledgeStore } from '../stores/knowledgeStore';
import { useAgentStore } from '../stores/agentStore';
import { useTaskStore } from '../stores/taskStore';
import { PageContainer, PageHeader } from '../components/common/page';
import { Card, Button, Badge } from '../components/common/ui';
export function DashboardPage() {
  useScreenInit();
  const models = useModelStore(s => s.models);
  const skills = useSkillStore(s => s.skills);
  const knowledgeBases = useKnowledgeStore(s => s.knowledgeBases);
  const agents = useAgentStore(s => s.agents);
  const tasks = useTaskStore(s => s.tasks);
  const ccModel = models.find(m => m.isClaudeCodeDefault);
  const failedTasks = tasks.filter(t => t.status === 'failed');
  const stats = [{
    label: '已配置模型',
    value: models.length,
    icon: BoxesIcon,
    to: '/models',
    tone: 'text-blue-400'
  }, {
    label: '已安装 Skills',
    value: skills.length,
    icon: PuzzleIcon,
    to: '/skills',
    tone: 'text-purple-400'
  }, {
    label: '知识库',
    value: knowledgeBases.length,
    icon: BookOpenIcon,
    to: '/knowledge-bases',
    tone: 'text-emerald-400'
  }, {
    label: '已创建 Agent',
    value: agents.length,
    icon: BotIcon,
    to: '/agents',
    tone: 'text-indigo-400'
  }];
  const failureReasons = [{
    reason: 'SHA256 校验失败',
    count: 3
  }, {
    reason: '模型连接超时',
    count: 2
  }, {
    reason: 'Skill manifest 缺失',
    count: 1
  }];
  return <PageContainer data-id="element-471">
      <PageHeader title="仪表盘" description="系统概览与快捷入口" action={<>
            <Link to="/models" data-id="element-473">
              <Button variant="outline" size="sm" data-id="element-474">
                <PlusIcon className="h-4 w-4" data-id="element-475" /> 添加模型
              </Button>
            </Link>
            <Link to="/agents/new" data-id="element-476">
              <Button size="sm" data-id="element-477">
                <PlusIcon className="h-4 w-4" data-id="element-478" /> 创建 Agent
              </Button>
            </Link>
          </>} data-id="element-472" />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-id="element-479">
        {stats.map(s => {
        const Icon = s.icon;
        return <Link key={s.label} to={s.to} data-id="element-480">
              <Card className="p-4 transition-colors hover:border-slate-700" data-id="element-481">
                <div className="flex items-center justify-between" data-id="element-482">
                  <Icon className={`h-5 w-5 ${s.tone}`} data-id="element-483" />
                  <ArrowRightIcon className="h-4 w-4 text-slate-600" data-id="element-484" />
                </div>
                <div className="mt-3 text-2xl font-semibold text-slate-100" data-id="element-485">
                  {s.value}
                </div>
                <div className="text-sm text-slate-400" data-id="element-486">{s.label}</div>
              </Card>
            </Link>;
      })}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3" data-id="element-487">
        <Card className="p-5 lg:col-span-2" data-id="element-488">
          <div className="flex items-center gap-2" data-id="element-489">
            <CpuIcon className="h-4 w-4 text-indigo-400" data-id="element-490" />
            <h3 className="text-sm font-semibold text-slate-200" data-id="element-491">
              Claude Code SDK 当前执行模型
            </h3>
          </div>
          {ccModel ? <div className="mt-3 flex items-center justify-between rounded-lg border border-slate-800 bg-slate-800/40 px-4 py-3" data-id="element-492">
              <div data-id="element-493">
                <div className="font-medium text-slate-100" data-id="element-494">{ccModel.name}</div>
                <div className="font-mono text-xs text-slate-500" data-id="element-495">
                  {ccModel.modelName}
                </div>
              </div>
              <Badge tone="indigo" data-id="element-496">{ccModel.provider}</Badge>
            </div> : <p className="mt-3 text-sm text-slate-500" data-id="element-497">
              尚未设置 Claude Code SDK 执行模型，请前往模型中心配置。
            </p>}
        </Card>

        <Card className="p-5" data-id="element-498">
          <div className="flex items-center gap-2" data-id="element-499">
            <AlertTriangleIcon className="h-4 w-4 text-red-400" data-id="element-500" />
            <h3 className="text-sm font-semibold text-slate-200" data-id="element-501">
              失败任务 ({failedTasks.length})
            </h3>
          </div>
          <div className="mt-3 text-xs text-slate-500" data-id="element-502">失败原因 Top 5</div>
          <ul className="mt-2 space-y-2" data-id="element-503">
            {failureReasons.map(f => <li key={f.reason} className="flex items-center justify-between text-sm" data-id="element-504">
                <span className="text-slate-300" data-id="element-505">{f.reason}</span>
                <span className="font-mono text-slate-500" data-id="element-506">{f.count}</span>
              </li>)}
          </ul>
          <Link to="/tasks" className="mt-4 inline-flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300" data-id="element-507">
            查看任务面板 <ArrowRightIcon className="h-3 w-3" data-id="element-508" />
          </Link>
        </Card>
      </div>
    </PageContainer>;
}